import {
  isAddonAvailableForSize,
  normalizeAddonSizePricing,
  resolveAddonPriceForSize,
} from "./productAddons";

export function createCustomOrderFieldId() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `cof_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function createCustomOrderOptionId() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `coo_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function defaultCustomOrderField(type = "text") {
  const id = createCustomOrderFieldId();
  if (type === "text") {
    return { id, type: "text", label: "" };
  }
  return {
    id,
    type,
    label: "",
    options: [{ id: createCustomOrderOptionId(), label: "", price: "" }],
  };
}

/** Options shown to customers — skip empty placeholder rows. */
export function visibleCustomOrderOptions(options) {
  return (Array.isArray(options) ? options : []).filter(
    (o) => String(o?.label || "").trim() || String(o?.price ?? "").trim() !== ""
  );
}

function normalizeCustomOrderOption(o, { forEditor = false } = {}) {
  const priceFallback = o?.price;
  let spInput = o?.size_pricing;
  if (o?.all_sizes === false && o?.by_size && typeof o.by_size === "object") {
    spInput = { all_sizes: false, by_size: o.by_size };
  } else if (o?.all_sizes === true) {
    spInput = { all_sizes: true };
  }
  const sp = normalizeAddonSizePricing(spInput, priceFallback);
  const out = {
    id: String(o?.id || "").trim() || createCustomOrderOptionId(),
    label: forEditor ? String(o?.label || "") : String(o?.label || "").trim(),
    price: o?.price === "" || o?.price == null ? "" : String(o?.price),
    all_sizes: sp.all_sizes,
    by_size: Object.fromEntries(
      Object.entries(sp.by_size).map(([k, v]) => [
        k,
        { ...v, price: v.price === "" || v.price == null ? "" : String(v.price) },
      ])
    ),
  };
  if (sp.all_sizes) {
    out.size_pricing = { all_sizes: true };
  } else if (Object.keys(sp.by_size).length) {
    out.size_pricing = { all_sizes: false, by_size: sp.by_size };
  }
  return out;
}

export function normalizeCustomOrderFields(raw, { forEditor = false } = {}) {
  const list = Array.isArray(raw) ? raw : [];
  return list
    .map((f) => {
      const id = String(f?.id || "").trim() || createCustomOrderFieldId();
      const type = String(f?.type || "text").trim();
      const label = forEditor ? String(f?.label || "") : String(f?.label || "").trim();
      const role = String(f?.role || "").trim() === "size" ? "size" : "";
      if (type === "text") {
        return { id, type: "text", label };
      }
      let options = (Array.isArray(f?.options) ? f.options : []).map((o) =>
        normalizeCustomOrderOption(o, { forEditor })
      );
      if (!forEditor) {
        options = visibleCustomOrderOptions(options);
      }
      const out = {
        id,
        type: type === "checkbox_group" ? "checkbox_group" : "select",
        label,
        options: options.length
          ? options
          : [{ id: createCustomOrderOptionId(), label: "", price: "" }],
      };
      if (role === "size" && out.type === "select") {
        out.role = "size";
      }
      return out;
    })
    .filter((f) => forEditor || f.label || f.type);
}

export function findCustomOrderSizeField(fields) {
  return normalizeCustomOrderFields(fields).find(
    (f) => f.type === "select" && f.role === "size"
  );
}

export function getCustomOrderSizes(fields) {
  return sortCustomOrderSelectOptions(findCustomOrderSizeField(fields)?.options || []).map(
    (o) => String(o.label || "").trim()
  ).filter(Boolean);
}

export function sortCustomOrderSelectOptions(options) {
  const visible = visibleCustomOrderOptions(options);
  return [...visible].sort((a, b) => {
    const pa = parseOptionPrice(a.price);
    const pb = parseOptionPrice(b.price);
    if (pa !== pb) return pa - pb;
    return String(a.label || "").localeCompare(String(b.label || ""), "ar");
  });
}

export function getSelectedCustomOrderSize(fields, formState) {
  const sizeField = findCustomOrderSizeField(fields);
  if (!sizeField) return null;
  const optionId = String(formState?.[sizeField.id]?.optionId ?? "").trim();
  const opt = (sizeField.options || []).find((o) => String(o.id) === optionId);
  return opt ? String(opt.label || "").trim() : null;
}

export function customOrderHasSizes(fields) {
  return getCustomOrderSizes(fields).length > 0;
}

export function resolveCustomOrderOptionPriceForSize(opt, selectedSize, hasSizes) {
  const addonLike = {
    price: opt?.price,
    size_pricing: opt?.size_pricing || {
      all_sizes: opt?.all_sizes !== false,
      by_size: opt?.by_size || {},
    },
  };
  return resolveAddonPriceForSize(addonLike, selectedSize, hasSizes);
}

export function isCustomOrderOptionAvailableForSize(opt, selectedSize, hasSizes) {
  const addonLike = {
    price: opt?.price,
    size_pricing: opt?.size_pricing || {
      all_sizes: opt?.all_sizes !== false,
      by_size: opt?.by_size || {},
    },
  };
  return isAddonAvailableForSize(addonLike, selectedSize, hasSizes);
}

export function parseOptionPrice(raw, fallback = 0) {
  const n = Number(raw);
  return Number.isFinite(n) ? Math.max(0, n) : fallback;
}

/** Build answers array from raw form state keyed by field id. */
export function buildCustomOrderAnswers(fields, formState) {
  const normalized = normalizeCustomOrderFields(fields);
  const hasSizes = customOrderHasSizes(normalized);
  const selectedSize = getSelectedCustomOrderSize(normalized, formState);
  const answers = [];
  for (const field of normalized) {
    const st = formState?.[field.id];
    if (field.type === "text") {
      const text = String(st?.text ?? st ?? "").trim();
      if (text) {
        answers.push({
          field_id: field.id,
          field_label: field.label,
          field_type: "text",
          text_value: text,
          price: 0,
        });
      }
      continue;
    }
    if (field.type === "select") {
      const optionId = String(st?.optionId ?? st ?? "").trim();
      const opt = (field.options || []).find((o) => String(o.id) === optionId);
      if (opt) {
        answers.push({
          field_id: field.id,
          field_label: field.label,
          field_type: "select",
          option_id: opt.id,
          option_label: opt.label,
          price: parseOptionPrice(opt.price),
        });
      }
      continue;
    }
    if (field.type === "checkbox_group") {
      const checked = Array.isArray(st?.checked) ? st.checked : Array.isArray(st) ? st : [];
      for (const optionId of checked) {
        const opt = (field.options || []).find((o) => String(o.id) === String(optionId));
        if (!opt) continue;
        if (
          hasSizes &&
          !isCustomOrderOptionAvailableForSize(opt, selectedSize, hasSizes)
        ) {
          continue;
        }
        const price =
          resolveCustomOrderOptionPriceForSize(opt, selectedSize, hasSizes) ??
          parseOptionPrice(opt.price);
        answers.push({
          field_id: field.id,
          field_label: field.label,
          field_type: "checkbox_group",
          option_id: opt.id,
          option_label: opt.label,
          price,
        });
      }
    }
  }
  return answers;
}

export function computeCustomOrderUnitPrice(settings, answers) {
  const base = parseOptionPrice(settings?.base_price, 0);
  const extra = (answers || []).reduce((s, a) => s + parseOptionPrice(a?.price, 0), 0);
  return Math.round((base + extra + Number.EPSILON) * 100) / 100;
}

export function validateCustomOrderForm(fields, formState) {
  const normalized = normalizeCustomOrderFields(fields);
  const missing = [];
  for (const field of normalized) {
    if (field.type === "text") {
      const text = String(formState?.[field.id]?.text ?? formState?.[field.id] ?? "").trim();
      if (!text) missing.push(field.label || "حقل نص");
      continue;
    }
    if (field.type === "select") {
      const optionId = String(
        formState?.[field.id]?.optionId ?? formState?.[field.id] ?? ""
      ).trim();
      if (!optionId) missing.push(field.label || "قائمة اختيار");
    }
  }
  return missing;
}

export function formatCustomOrderSummary(settings, answers) {
  const lines = [];
  const fields = normalizeCustomOrderFields(settings?.fields);
  for (const field of fields) {
    const rows = (answers || []).filter((a) => String(a.field_id) === String(field.id));
    if (field.type === "text") {
      const text = rows[0]?.text_value || "";
      if (text) lines.push(`${field.label}: ${text}`);
      continue;
    }
    if (field.type === "select") {
      const row = rows[0];
      if (row?.option_label) {
        const pr = parseOptionPrice(row.price);
        lines.push(`${field.label}: ${row.option_label}${pr ? ` (+₪${pr})` : ""}`);
      }
      continue;
    }
    if (field.type === "checkbox_group" && rows.length) {
      const parts = rows.map((r) => {
        const pr = parseOptionPrice(r.price);
        return `${r.option_label}${pr ? ` (+₪${pr})` : ""}`;
      });
      lines.push(`${field.label}: ${parts.join("، ")}`);
    }
  }
  return lines;
}

export function customOrderNoteText(settings, answers) {
  const title = String(settings?.cart_item_name || settings?.title || "طلب مخصص").trim();
  const lines = formatCustomOrderSummary(settings, answers);
  return [title, ...lines.map((l) => `• ${l}`)].join("\n");
}

export function customOrderAddonsFromAnswers(answers) {
  return (answers || [])
    .filter((a) => a.field_type === "checkbox_group" && a.option_id)
    .map((a) => ({
      id: String(a.option_id),
      name: String(a.option_label || ""),
      price: parseOptionPrice(a.price),
    }));
}

export function customOrderMergeKey(sectionId, answers) {
  const parts = (answers || [])
    .map((a) => {
      if (a.field_type === "text") {
        return `t:${a.field_id}=${String(a.text_value || "").trim()}`;
      }
      return `o:${a.field_id}=${String(a.option_id || "").trim()}`;
    })
    .sort();
  return `custom:${String(sectionId)}|${parts.join("|")}`;
}

export function serializeCustomOrderAnswersForApi(answers) {
  return (answers || []).map((a) => {
    const out = { field_id: String(a.field_id || "").trim() };
    if (a.field_type === "text") {
      out.text_value = String(a.text_value || "").trim();
    } else if (a.option_id) {
      out.option_id = String(a.option_id).trim();
    }
    return out;
  });
}
